import React, { useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    FormHelperText,
    Paper,
    Stack,
    TextField,
    Tooltip,
} from "@mui/material";
import styled from "styled-components";
import Auth from "src/services/auth";
import { LoadingButton } from "@mui/lab";
import Icon from "src/components/icon";
import googleOneIcon from "src/assets/icons/google-one-icon";
import githubColor from "src/assets/icons/github-color";

const CustomPaper = styled(Paper)`
    border-radius: 14px;
    background: white;
    min-height: 100px;
    min-width: 100px;
    padding: 16px;
    font-size: 16px;
    color: #2d333a;
`;

const ButtonCustom = styled(LoadingButton)`
    text-transform: none;
    color: white;
    font-size: 16px;

    background-color: #0059d6;

    &:hover {
        background-color: #0050c0;
    }

    &:disabled {
        background-color: #0059d6;
    }

    &:active {
        background-color: #0050c0;
    }
`;
const ButtonWithBorder = styled(Button)(({ loading, off }) => ({
    minHeight: "52px",
    textTransform: "none",
    padding: 0,
    fontSize: "16px",
    color: loading || off ? "grey" : "rgb(45, 51, 58)",
    ...(off && { backgroundColor: "lightgrey" }),
    pointerEvents: loading || off ? "none" : "auto",
}));

const IconContainer = styled.div`
    height: 52px;
    width: 52px;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const Line = styled.div`
    display: flex;
    padding: 15px;

    &:before {
        display: block;
        content: "";
        border-bottom: 1px solid #c2c8d0;
        flex: 1 0 auto;
        height: 0.5em;
        margin: 0;
    }

    &:after {
        display: block;
        content: "";
        border-bottom: 1px solid #c2c8d0;
        flex: 1 0 auto;
        height: 0.5em;
        margin: 0;
        box-sizing: border-box;
        -webkit-font-smoothing: antialiased;
    }
    & span {
        margin: 0 10px 0 10px;
        text-transform: uppercase;
        font-size: 12px;
    }
`;

const CssTextField = styled(TextField)({
    color: "grey",
    "& .MuiOutlinedInput-root": {
        "& fieldset": {
            borderColor: "grey",
        },
        "&:hover fieldset": {
            borderColor: "grey",
        },
        "&.Mui-focused fieldset": {
            borderColor: "grey",
        },
    },
});

const Container = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 100vh;
    background: #000;
`;

const emailChecker = (input) => {
    const regexEmail = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

    return regexEmail.test(input);
};

const LogInScreenUsername = () => {
    const [username, setUsername] = useState("");
    const [error, setError] = useState();
    const [redirecting, setRedirecting] = useState(false);
    const [googleRedirect, setGoogleRedirect] = useState(false);
    const [githubRedirect, setGithubRedirect] = useState(false);

    const check = () => {
        const isEmail = emailChecker(username);
        if (!isEmail) {
            setError("Please enter a valid email");
        }
        return isEmail;
    };

    const handleChange = (event) => {
        setUsername(event.target.value);
        const isEmail = emailChecker(event.target.value);
        if (isEmail && error) {
            setError();
        }
    };

    const handleBlur = () => {
        check();
    };

    return (
        <Box>
            <Container>
                <div
                    style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        margin: "20px 0 20px 0",
                        paddingBottom: "50px",
                    }}
                >
                    <CustomPaper>
                        <Stack
                            title="Adadot"
                            id="custom-prompt-logo"
                            direction="column"
                            spacing={2}
                            alignItems="center"
                            style={{
                                margin: "40px 40px 24px 40px",
                            }}
                        >
                            <img
                                id="prompt-logo-center"
                                src="https://assets.adadot.com/adadot-icon-green.png"
                                alt="Adadot"
                                style={{
                                    display: "block",
                                    objectFit: "contain",
                                    maxHeight: "52px",
                                    maxWidth: "100%",
                                    margin: "0 auto",
                                }}
                            />

                            <h1
                                style={{
                                    fontSize: "24px",
                                    fontWeight: 400,
                                }}
                            >
                                Welcome
                            </h1>

                            <div className="c31b86380 ca3fde804">
                                <p style={{ fontSize: "14px" }}>Login to Adadot</p>
                            </div>
                        </Stack>
                        <Stack direction="column" spacing={2}>
                            <FormControl error={!!error} style={{ width: "320px", color: "grey" }}>
                                <CssTextField
                                    label="Email address*"
                                    value={username}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    fullWidth
                                    style={{ color: "grey" }}
                                    sx={{ input: { color: "grey" } }}
                                    InputLabelProps={{
                                        style: { color: "grey" },
                                    }}
                                    onKeyDown={(event) => {
                                        if (event.type === "keydown" && event.key === "Enter") {
                                            if (username && !error && check()) {
                                                setRedirecting(true);
                                                window.location.replace(
                                                    Auth.getLoginUrl("/", username)
                                                );
                                            }
                                        }
                                    }}
                                />
                                <FormHelperText error>{error || " "}</FormHelperText>
                            </FormControl>
                            <Tooltip title={!username ? "Enter an email to continue" : ""}>
                                <span>
                                    <ButtonCustom
                                        type="submit"
                                        variant="contained"
                                        size="medium"
                                        style={{
                                            width: "320px",
                                            minHeight: "52px",
                                            textAlign: "center",
                                        }}
                                        fullWidth
                                        disabled={
                                            !username || !!error || googleRedirect || githubRedirect
                                        }
                                        onClick={() => {
                                            if (username && !error && check()) {
                                                setRedirecting(true);
                                                window.location.replace(
                                                    Auth.getLoginUrl("/", username)
                                                );
                                            }
                                        }}
                                        loading={redirecting}
                                    >
                                        Continue with email
                                    </ButtonCustom>
                                </span>
                            </Tooltip>
                            <Line>
                                <span>Or</span>
                            </Line>
                            <ButtonWithBorder
                                variant="outlined"
                                fullWidth
                                color="primary"
                                href={Auth.getLoginUrl("/", username, "google-oauth2")}
                                onClick={() => setGoogleRedirect(true)}
                                loading={googleRedirect}
                                off={redirecting || githubRedirect}
                            >
                                {googleRedirect ? (
                                    <CircularProgress />
                                ) : (
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        width="100%"
                                        justifyContent="flex-start"
                                    >
                                        <IconContainer>
                                            <Icon svg={googleOneIcon} width="20px" height="20px" />
                                        </IconContainer>
                                        <span>Continue with Google</span>
                                    </Stack>
                                )}
                            </ButtonWithBorder>
                            <ButtonWithBorder
                                variant="outlined"
                                fullWidth
                                color="primary"
                                href={Auth.getLoginUrl("/", username, "github")}
                                onClick={() => setGithubRedirect(true)}
                                loading={githubRedirect}
                                off={redirecting || googleRedirect}
                            >
                                {githubRedirect ? (
                                    <CircularProgress />
                                ) : (
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        width="100%"
                                        justifyContent="flex-start"
                                    >
                                        <IconContainer>
                                            <Icon svg={githubColor} width="20px" height="20px" />
                                        </IconContainer>
                                        <span>Continue with Github</span>
                                    </Stack>
                                )}
                            </ButtonWithBorder>
                        </Stack>
                    </CustomPaper>
                </div>
            </Container>
        </Box>
    );
};

export default LogInScreenUsername;
